import type { FormEvent } from 'react';

import { useRouteLoaderData } from '@remix-run/react';
import { CardElement } from '@stripe/react-stripe-js';
import type { Stripe, StripeElements } from '@stripe/stripe-js';

import type { RootLoaderData } from '~/root';

import { appendNectarUpgradeParams } from '../utils/appendNectarUpgradeParams';
import { createPaidSubscription } from '../utils/createPaidSubscription';

interface HandleSubmitWithPaymentElementOptions {
  event: FormEvent;
  stripeSession: Stripe;
  elements: StripeElements;
  email: string;
  defaultErrorMessage: string;
}

interface ReturnProps {
  handleSubmit: (
    event: FormEvent,
    fetcher: any,
    stripeSession: Stripe,
    elements: StripeElements,
    email: string,
  ) => Promise<void>;
  handleSubmitWithPaymentElement: (
    options: HandleSubmitWithPaymentElementOptions,
  ) => Promise<{ redirectToSSOLogin: boolean }>;
}

const useInternalCheckout = (): ReturnProps => {
  const { site } = useRouteLoaderData('root') as RootLoaderData;

  const handleSubmit = async (
    event: FormEvent,
    fetcher: any,
    stripeSession: Stripe,
    elements: StripeElements,
    email: string,
  ) => {
    const card = elements.getElement(CardElement);
    if (!card) {
      throw new Error('Something went wrong while processing your card details, please try again');
    }

    const result = await stripeSession.createToken(card);

    if (result.error) {
      throw new Error(
        result.error?.message || 'Something went wrong while processing your card details, please try again',
      );
    }

    const paymentMethod = await stripeSession.createPaymentMethod({
      type: 'card',
      card,
      billing_details: {
        email,
      },
    });

    const formData = new FormData(event.target as HTMLFormElement);
    if (paymentMethod?.paymentMethod?.id) {
      formData.append('payment_method', paymentMethod.paymentMethod.id);
    }
    appendNectarUpgradeParams(formData, site.id);

    fetcher.submit(formData, { method: 'post', action: '/upgrade' });
  };

  const handleSubmitWithPaymentElement = async ({
    event,
    stripeSession,
    elements,
    email,
    defaultErrorMessage,
  }: HandleSubmitWithPaymentElementOptions) => {
    const result = await elements.submit();
    if (result.error) {
      throw new Error(
        result.error?.message || 'Something went wrong while processing your card details, please try again',
      );
    }

    const { paymentMethod, error } = await stripeSession.createPaymentMethod({
      elements,
      params: {
        billing_details: {
          email,
        },
      },
    });

    if (error) {
      throw new Error(error?.message);
    }

    const formData = new FormData(event.target as HTMLFormElement);
    formData.append('payment_method', paymentMethod.id);
    appendNectarUpgradeParams(formData, site.id);

    const response = await createPaidSubscription({
      formData,
    });

    if (!response || response.toast?.status === 'error') {
      throw new Error(response?.toast?.message || defaultErrorMessage);
    }

    if (response.action && response.clientSecret) {
      // Setup intent
      if (response.object === 'setup_intent') {
        const { error } = await stripeSession.confirmSetup({
          elements,
          clientSecret: response.clientSecret,
          redirect: 'if_required',
          confirmParams: {
            return_url: window.location.href,
          },
        });

        if (error) {
          throw new Error(error?.message);
        }
      } else if (response.object === 'payment_intent') {
        const { error } = await stripeSession.confirmPayment({
          elements,
          clientSecret: response.clientSecret,
          redirect: 'if_required',
          confirmParams: {
            return_url: window.location.href,
          },
        });

        if (error) {
          throw new Error(error?.message);
        }
      } else {
        throw new Error(defaultErrorMessage);
      }
    }

    // Subscribed!

    return {
      redirectToSSOLogin: response?.redirectToSSOLogin ?? false,
    };
  };

  return {
    handleSubmit,
    handleSubmitWithPaymentElement,
  };
};

export default useInternalCheckout;
